import type { MountedExperiment, SliderRowHandle } from '@learnforge/demo-kit';
import { createSliderRow } from '@learnforge/demo-kit';
import { evaluate, generateVerbalDescription, parse, sampleCurve, type ExprNode } from '../func';
import { createActionBar } from '../action-bar';
import { createFunctionGraph, type FunctionGraphHandle } from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportHandle,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import { createTableView, type TableViewHandle } from '../render/table-view';
import { createVerbalPane, type VerbalPaneHandle } from '../render/verbal-pane';
import type { FuncContext } from '../types';

export interface TableRow {
  x: number;
  y: number;
}

export function buildTable(
  node: ExprNode,
  start: number,
  end: number,
  step: number,
  precision: number,
): TableRow[] {
  const rows: TableRow[] = [];
  for (let x = start; x <= end + 1e-9; x += step) {
    const xRounded = Number(x.toFixed(precision));
    const y = evaluate(node, xRounded);
    rows.push({ x: xRounded, y });
  }
  return rows;
}

export function mountRepresentations(ctx: FuncContext): MountedExperiment {
  let fSrc = 'x^2 - 2*x - 1';
  let range: { start: number; end: number; step: number } = { start: -2, end: 4, step: 1 };
  let precision = 2;

  const container = document.createElement('div');
  container.className = 'fn-representations-container';
  ctx.root.appendChild(container);

  // LEFT: chart column (full height)
  const graphCol = document.createElement('div');
  graphCol.className = 'fn-representations-col fn-representations-chart';
  const graphTitle = document.createElement('h4');
  graphTitle.textContent = ctx.i18n.t('panel.graph');
  graphCol.appendChild(graphTitle);
  const graphPlot = document.createElement('div');
  graphPlot.className = 'fn-representations-plot';
  graphCol.appendChild(graphPlot);
  const svg = createSvgEl('svg', { viewBox: '0 0 500 600', class: 'fn-stage-mini' });
  graphPlot.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(graphPlot);
  let view: CartesianViewportView = {
    xDomain: [range.start, range.end],
    yDomain: [-10, 10],
    zoom: 1,
  };
  let viewport: CartesianViewportHandle | null = null;
  let graphHandle: FunctionGraphHandle | null = null;

  // RIGHT: stacked column with [formula | verbal] on top, table below
  const rightCol = document.createElement('div');
  rightCol.className = 'fn-representations-right';

  const topRow = document.createElement('div');
  topRow.className = 'fn-representations-top';

  const formulaCol = document.createElement('div');
  formulaCol.className = 'fn-representations-col';
  const formulaTitle = document.createElement('h4');
  formulaTitle.textContent = ctx.i18n.t('panel.formula');
  formulaCol.appendChild(formulaTitle);
  const formulaInput = document.createElement('input');
  formulaInput.type = 'text';
  formulaInput.value = fSrc;
  formulaInput.addEventListener('change', () => {
    try {
      parse(formulaInput.value);
      fSrc = formulaInput.value;
      formulaInput.classList.remove('fn-input-error');
      redraw();
    } catch {
      formulaInput.classList.add('fn-input-error');
    }
  });
  formulaCol.appendChild(formulaInput);

  const verbalCol = document.createElement('div');
  verbalCol.className = 'fn-representations-col';
  const verbalTitle = document.createElement('h4');
  verbalTitle.textContent = ctx.i18n.t('panel.verbal');
  verbalCol.appendChild(verbalTitle);
  const verbalHost = document.createElement('div');
  verbalCol.appendChild(verbalHost);
  const verbalHandle: VerbalPaneHandle = createVerbalPane(verbalHost, { text: '' });

  topRow.append(formulaCol, verbalCol);

  const tableCol = document.createElement('div');
  tableCol.className = 'fn-representations-col fn-representations-table';
  const tableTitle = document.createElement('h4');
  tableTitle.textContent = ctx.i18n.t('panel.table');
  tableCol.appendChild(tableTitle);
  const tableHost = document.createElement('div');
  tableCol.appendChild(tableHost);
  const tableHandle: TableViewHandle = createTableView(tableHost, {
    rows: [],
    precision,
    onXEdit: () => {},
  });

  rightCol.append(topRow, tableCol);
  container.append(graphCol, rightCol);

  function redraw(): void {
    const node = parse(fSrc).node;
    const rows = buildTable(node, range.start, range.end, range.step, precision);
    tableHandle.update({ rows, precision, onXEdit: () => {} });
    const sampleCount = Math.max(320, Math.round(500 * (globalThis.devicePixelRatio || 1)));
    const sample = sampleCurve(node, view.xDomain[0], view.xDomain[1], sampleCount);
    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 500,
      height: 600,
      curves: [{ sample, className: 'fn-curve-primary', label: 'f(x)' }],
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    legend.setEntries([{ id: 'function', formula: `f(x) = ${fSrc}`, color: 'var(--accent)' }]);
    verbalHandle.update({ text: generateVerbalDescription(node) });
  }

  const sliderHandles: Record<keyof typeof range, SliderRowHandle> = {} as Record<
    keyof typeof range,
    SliderRowHandle
  >;
  function addSlider(
    labelKey: string,
    key: keyof typeof range,
    min: number,
    max: number,
    step: number,
  ): void {
    const handle = createSliderRow({
      id: `rep-${key}`,
      labelKey,
      i18n: ctx.i18n,
      min,
      max,
      step,
      value: range[key],
      format: (v) => (step >= 1 ? v.toFixed(0) : v.toFixed(2)),
      onChange: (v) => {
        range[key] = v;
        if (key === 'start' || key === 'end') {
          viewport?.setBase({ xDomain: [range.start, range.end], yDomain: [-10, 10] });
        } else {
          redraw();
        }
      },
    });
    sliderHandles[key] = handle;
    ctx.panel.appendChild(handle.element);
  }
  addSlider('param.range-start.label', 'start', -10, 0, 1);
  addSlider('param.range-end.label', 'end', 0, 10, 1);
  addSlider('param.range-step.label', 'step', 0.1, 2, 0.1);

  const precisionRow = document.createElement('div');
  precisionRow.className = 'fn-panel-row fn-dropdown-row';
  const precisionLabel = document.createElement('label');
  precisionLabel.textContent = ctx.i18n.t('param.precision.label');
  const precisionSelect = document.createElement('select');
  for (let i = 0; i <= 4; i++) {
    const opt = document.createElement('option');
    opt.value = String(i);
    opt.textContent = String(i);
    precisionSelect.appendChild(opt);
  }
  precisionSelect.value = String(precision);
  precisionSelect.addEventListener('change', () => {
    precision = Number(precisionSelect.value);
    redraw();
  });
  precisionRow.appendChild(precisionLabel);
  precisionRow.appendChild(precisionSelect);
  ctx.panel.appendChild(precisionRow);

  redraw();
  viewport = createCartesianViewport(svg, {
    base: { xDomain: [range.start, range.end], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 500, height: 600 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          fSrc = 'x^2 - 2*x - 1';
          range = { start: -2, end: 4, step: 1 };
          precision = 2;
          formulaInput.value = fSrc;
          precisionSelect.value = '2';
          sliderHandles.start.setValue(range.start);
          sliderHandles.end.setValue(range.end);
          sliderHandles.step.setValue(range.step);
          const currentView = viewport?.getView();
          viewport?.setBase(
            { xDomain: [range.start, range.end], yDomain: [-10, 10] },
            { notify: false },
          );
          if (currentView) viewport?.setView(currentView);
          else redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport?.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      viewport?.dispose();
      legend.destroy();
      tableHandle.destroy();
      verbalHandle.destroy();
      container.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
